import { Mobile } from '@bimser/common';
import {
	BCMobileTimePicker,
	BCNotification,
	BCTimePicker,
	BCTimePickerRef,
	findMessage,
	IntRange,
	ITimePickerProps
} from '@bimser/components';
import { createGuid, FormItemTypes, IDictionary, MLHelper, mobile } from '@bimser/core';
import classNames from 'classnames/bind';
import dayjs from 'dayjs';
import localeData from 'dayjs/plugin/localeData';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import updateLocale from 'dayjs/plugin/updateLocale';
import _isEmpty from 'lodash/isEmpty';
import * as React from 'react';
import { IPropertyField, SizeType } from '../../../../entities/common';
import EditControl from '../../base/EditControl';
import { IPropertyChangedEventArgs } from '../../entities/EventArgs';
import Styles from './assets/styles.scss';
import ITimePickerProperties from './entities/ITimePickerProperties';
dayjs.extend(localizedFormat);
dayjs.extend(localeData);
dayjs.extend(updateLocale);

const cx = classNames.bind(Styles);

export default class TimePicker extends EditControl<string> {
	format: string;
	disabledHours: Array<string | number>;
	allowClear: boolean;
	use12Hours: boolean;
	hourStep: IntRange<1, 23>;
	minuteStep: IntRange<1, 59>;
	secondStep: IntRange<1, 59>;
	sizeType: SizeType;
	inputRef = React.createRef<BCTimePickerRef>();

	getText(value: string = this.value) {
		const date = dayjs(value);

		if (!date.isValid()) {
			return '';
		}

		return date.format(this.format || dayjs.localeData().longDateFormat('LTS'));
	}

	get text() {
		return this.getText();
	}

	set text(value: string) {
		if (value) {
			const date = dayjs(value);

			if (date.isValid()) {
				this.__proxyChanging.value = date.format();
			}
		}
	}

	constructor() {
		super();

		this.controlType = FormItemTypes.EControlTypes.TimePicker;
		this.disabledHours = [];
		this.use12Hours = false;
		this.hourStep = 1;
		this.minuteStep = 1;
		this.secondStep = 1;
		this.allowClear = true;

		dayjs.updateLocale(MLHelper.currentLanguage.twoLetterCulture, {
			meridiem: (hour: number) =>
				hour < 12 ? findMessage.get('102777') : findMessage.get('102778')
		});

		this.getDisabledHours = this.getDisabledHours.bind(this);

		this.subscribe('trigger:OnValueChanged', (value: string) => {
			//
			if (_isEmpty(value)) {
				this.triggerValueChanged(undefined, null);
			} else {
				this.triggerValueChanged(undefined, value);
			}
		});
	}

	private getDisabledHours(): number[] {
		return this.disabledHours.map(i => Number(i));
	}

	__loadProperties(data: ITimePickerProperties) {
		super.__loadProperties(data);
		this.__proxyChanged.format = data.format || (data.use12Hours ? 'hh:mm:ss a' : 'HH:mm:ss');
		this.__proxyChanged.disabledHours = data.disabledHours;
		this.__proxyChanged.use12Hours = data.use12Hours;
		this.__proxyChanged.hourStep = data.hourStep;
		this.__proxyChanged.minuteStep = data.minuteStep;
		this.__proxyChanged.secondStep = data.secondStep;
		this.__proxyChanged.allowClear = data.allowClear;
		this.__proxyChanged.sizeType = data.sizeType;
	}

	__loadValueProperty(newValue: string) {
		const oldValue = this.value;
		this.__proxyChanged.value = newValue;

		if (this.__isInitialized) {
			const argument: IPropertyChangedEventArgs<string> = {
				name: this.name,
				newValue,
				oldValue
			};

			this.emit('event:OnValueChanged', argument);
			this.emit('event:server:OnValueChanged', {
				argument,
				controlId: this.name,
				name: 'ValueChanged',
				sender: 'web'
			});

			const newText = this.getText(newValue);
			const oldText = this.getText(oldValue);

			if (newText !== oldText && this.__isInitialized) {
				const argument: IPropertyChangedEventArgs<string> = {
					name: this.name,
					newValue: newText,
					oldValue: oldText
				};

				this.emit('event:OnTextChanged', argument);
				this.emit('event:server:OnTextChanged', {
					argument,
					controlId: this.name,
					name: 'TextChanged',
					sender: 'web'
				});
			}
		}
	}

	__loadTextProperty() {
		// override
	}

	protected triggerValueChanged(date: dayjs.Dayjs, isoZoneString: string) {
		if (this.__form.__fromMobile) {
			date = dayjs(isoZoneString);
		}

		const isHourDisabled = this.disabledHours?.some(h => h == date?.hour());
		if (isHourDisabled) {
			BCNotification.warning({
				autoClose: 3000,
				message: findMessage.get('102654'),
				description: findMessage
					.get('102655')
					.replace('$1', String(date.hour()))
					.replace('$2', findMessage.get('100348'))
			});

			const message: any = {
				typeName: 'ShowMessageAction',
				dialogType: 'Toaster',
				alertType: 'Warning',
				id: createGuid(),
				messages: findMessage
					.get('102655')
					.replace('$1', String(date.hour()))
					.replace('$2', findMessage.get('100348')),
				name: findMessage.get('102654'),
				enabled: true
			};
			Mobile.MobileView.mobileClientSideMessages([message]);
		} else {
			this.__proxyChanging.value = isoZoneString || '';
		}
	}

	focus() {
		this.inputRef.current?.focus();
	}

	__getPropertyFields(properties?: Map<string, any>): IPropertyField[] {
		const fields = super.__getPropertyFields();

		const format = properties.get('format');
		const use12Hours = properties.get('use12Hours');

		return [
			...fields,
			{
				name: 'value',
				type: 'time',
				options: {
					format: format || (use12Hours ? 'hh:mm:ss a' : 'HH:mm:ss'),
					use12Hours
				}
			},
			{
				name: 'format',
				type: 'string'
			},
			{
				name: 'use12Hours',
				type: 'boolean'
			},
			{
				name: 'hourStep',
				type: 'number'
			},
			{
				name: 'minuteStep',
				type: 'number'
			},
			{
				name: 'secondStep',
				type: 'number'
			}
		];
	}

	__getComponentData(): ITimePickerProps & IDictionary<any> {
		const format = this.format || dayjs().localeData().longDateFormat('LTS');
		return {
			...super.__getComponentData(),
			use12Hours: this.use12Hours,
			hourStep: this.hourStep,
			minuteStep: this.minuteStep,
			secondStep: this.secondStep,
			disabledHours: this.disabledHours.map(i => Number(i)) as any,
			format: format,
			disabled: this.__isDisabled(),
			value: this.value ? (dayjs(this.value).format(format) as any) : null, //dayjs(dayjs.utc(this.value).toDate())
			style: { width: '100%', ...this.style },
			size: this.sizeType,
			allowClear: this.allowClear && !this.readOnly
		};
	}

	__getSizeType() {
		return this.sizeType ?? this.__form.sizeType;
	}

	__getComponentByPlatform() {
		const format = this.format || dayjs().localeData().longDateFormat('LTS');
		const className = cx({
			transparentContent: true,
			mobileClear: Mobile.MobileView.isAndroidAvaliable || Mobile.MobileView.isIosAvaliable
		});

		if (mobile().any()) {
			return (
				<BCMobileTimePicker
					tabIndex={this.tabIndex}
					use12Hours={this.use12Hours}
					hourStep={this.hourStep}
					minuteStep={this.minuteStep}
					secondStep={this.secondStep}
					disabledHours={this.getDisabledHours}
					format={format}
					disabled={this.__isDisabled()}
					placeholder={this.placeholder.getText()}
					value={this.value ? dayjs(this.value) : null}
					style={{ width: '100%', ...this.style }}
					className={className}
					onChange={this.triggerValueChanged.bind(this)}
					size={this.__getSizeType()}
					readOnly={this.__isReadOnly()}
					allowClear={this.allowClear}
					textAlign={this.textAlign}
					inputReadOnly={!!mobile().any()}
				/>
			);
		}
		return (
			<BCTimePicker
				ref={this.inputRef}
				tabIndex={this.tabIndex}
				use12Hours={this.use12Hours}
				hourStep={this.hourStep}
				minuteStep={this.minuteStep}
				secondStep={this.secondStep}
				disabledHours={this.getDisabledHours}
				format={format}
				disabled={this.__isDisabled()}
				placeholder={this.placeholder.getText()}
				value={this.value ? dayjs(this.value) : null}
				style={{ width: '100%', ...this.style }}
				className={className}
				onChange={this.triggerValueChanged.bind(this)}
				size={this.__getSizeType()}
				readOnly={this.__isReadOnly()}
				allowClear={this.allowClear}
				textAlign={this.textAlign}
				inputReadOnly={!!mobile().any()}
			/>
		);
	}

	__render(children?: React.ReactNode) {
		return super.__render(this.__getComponentByPlatform());
	}
}
